import { useCallback, useMemo } from 'react';

import { WRAPPER_TO_PROVIDER_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { Provider } from '@/features/game-context/types';
import { handleUnknownEvent } from '@/features/game-window-integration/helpers/generic-helpers';
import {
  PragmaticEventData,
  PragmaticEvents,
} from '@/features/game-window-integration/providers/pragmatic/types';
import { usePragmaticHandlers } from '@/features/game-window-integration/providers/pragmatic/use-pragmatic-handlers';
import { ProviderLoggerMessages } from '@/features/game-window-integration/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';

export const usePragmaticEventHandlers = () => {
  const {
    getGameProviderFrame,
    handleGameReady,
    handleShowDeposit,
    handleSpinInProgress,
    handleError,
    handleCloseGame,
    handleVolumeChange,
    handleGameChange,
  } = usePragmaticHandlers();

  const providerEventHandlerMapper = useMemo(
    () => ({
      [PragmaticEvents.GAME_READY]: handleGameReady,
      [PragmaticEvents.ROUND_START]: handleSpinInProgress,
      [PragmaticEvents.ROUND_END]: handleSpinInProgress,
      [PragmaticEvents.SHOW_DEPOSIT]: handleShowDeposit,
      [PragmaticEvents.HOME_REDIRECT]: handleCloseGame,
      [PragmaticEvents.INSUFFICIENT_FUNDS]: handleError,
      [PragmaticEvents.SYSTEM_MESSAGE_SHOWN]: handleError,
      [PragmaticEvents.SOUND_OFF]: handleVolumeChange,
      [PragmaticEvents.SOUND_ON]: handleVolumeChange,
      [PragmaticEvents.GAME_CHANGE]: handleGameChange,
    }),
    [
      handleGameReady,
      handleShowDeposit,
      handleSpinInProgress,
      handleCloseGame,
      handleError,
      handleVolumeChange,
      handleGameChange,
    ],
  );

  const providerEventHandler = useCallback(
    ({ data }: { data: PragmaticEventData }) => {
      const eventType = data?.name || data?.event;

      if (eventType) {
        if (providerEventHandlerMapper[eventType]) {
          providerEventHandlerMapper[eventType]?.(data);
        } else {
          handleUnknownEvent(data);
        }
      }
    },
    [providerEventHandlerMapper],
  );

  const wrapperEventHandler = useCallback(
    (e: MessageEvent) => {
      const executeEvent = (event: PragmaticEventData | PragmaticEvents) => {
        const gameProviderFrame = getGameProviderFrame();
        const options = { targetOrigin: '*' };

        if (gameProviderFrame) {
          gameProviderFrame.postMessage(event, options);
        } else {
          Logger.rumError({
            name: ProviderLoggerMessages.FAILED_HANDLER_FETCH.replace(
              '%{provider}',
              Provider.PRAGMATIC,
            ),
            useCase: DdEventCategory.ProviderIntegration,
            eventType: DdEventType.Error,
            context: {
              provider: Provider.PRAGMATIC,
            },
          });
        }
      };

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE) {
        executeEvent({
          event: PragmaticEvents.UPDATE_BALANCE,
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_STOP) {
        executeEvent(PragmaticEvents.AUTO_SPIN_STOP);
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO) {
        executeEvent({
          event: PragmaticEvents.SET_VOLUME,
          params: {
            muted: !e.data.payload,
          },
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_PAYTABLE) {
        executeEvent({
          name: PragmaticEvents.TOGGLE_PAYTABLE,
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_HELP) {
        executeEvent({
          name: PragmaticEvents.TOGGLE_HELP,
        });
      }
    },
    [getGameProviderFrame],
  );

  return { wrapperEventHandler, providerEventHandler };
};
